import { useEffect, useMemo, useRef, useState } from "react";
import { Phone } from "lucide-react";
import { useAppTranslation } from "@/i18n/useAppTranslation";
import { cn } from "@/lib/utils";
import {
  COUNTRY_DIAL_CODES,
  defaultDialForLocale,
  flagEmoji,
  preferredCountryFromLocale,
} from "@/lib/countryDialCodes";
import { getStoredMarket, marketToCountry } from "@/i18n/markets";

// Bug fix (inbox feedback, May 2026): this dropdown previously only
// listed countries in `BOOKABLE_COUNTRY_ISOS` (the ~15 markets where
// we currently accept listings/bookings). That made sense for the
// car's *location*, but this is a *phone* country code picker —
// renters travel, hosts have foreign-issued mobile numbers, and a
// German owner whose phone is a UK SIM was being blocked from
// even entering their number. We now show the full ISO list and
// just sort the user's preferred country to the top so the common
// case still defaults to the right pick.

export function usePreferredDial() {
  const { i18n } = useAppTranslation();
  return useMemo(() => {
    const fromMarket = marketToCountry(getStoredMarket());
    const preferredIso = fromMarket || preferredCountryFromLocale(i18n.language);
    if (preferredIso) {
      const found = COUNTRY_DIAL_CODES.find((c) => c.code === preferredIso);
      if (found) return found.dial;
    }
    return defaultDialForLocale(i18n.language);
  }, [i18n.language]);
}

export interface PhoneNumberInputProps {
  dialCode: string;
  onDialCodeChange: (dial: string) => void;
  localNumber: string;
  onLocalNumberChange: (val: string) => void;
  inputId?: string;
  autoFocus?: boolean;
  required?: boolean;
  placeholder?: string;
  className?: string;
}

export function PhoneNumberInput({
  dialCode,
  onDialCodeChange,
  localNumber,
  onLocalNumberChange,
  inputId,
  autoFocus,
  required,
  placeholder = "555 123 4567",
  className,
}: PhoneNumberInputProps) {
  const { t, i18n } = useAppTranslation();
  const preferredIso = useMemo(() => {
    const fromMarket = marketToCountry(getStoredMarket());
    return fromMarket || preferredCountryFromLocale(i18n.language);
  }, [i18n.language]);

  const countries = useMemo(() => {
    if (!preferredIso) return COUNTRY_DIAL_CODES;
    const upper = preferredIso.toUpperCase();
    const preferred = COUNTRY_DIAL_CODES.find((c) => c.code === upper);
    if (!preferred) return COUNTRY_DIAL_CODES;
    return [preferred, ...COUNTRY_DIAL_CODES.filter((c) => c.code !== upper)];
  }, [preferredIso]);

  // Bug fix (inbox feedback, May 2026): picking "United States (+1)"
  // reverted to "Antigua and Barbuda (+1)" because the <select> was
  // keyed on dial code. Several countries share +1 (US, Canada, Antigua,
  // Bahamas, ...), so when React reconciled `value="+1"` it fell back
  // to the FIRST <option> with that value (alphabetically, AG). The
  // parent only stored the dial, so it couldn't disambiguate either.
  //
  // Fix: track the picked ISO in local state and key <option> on ISO.
  // We still emit just the dial to the parent (API unchanged).
  //
  // Reconciliation is intentionally narrow: we only re-derive
  // selectedIso from dialCode when the *parent* hands us a new
  // dialCode value that we did NOT just emit ourselves. This avoids
  // a race where, on shared dials, our own onDialCodeChange("+1")
  // could otherwise re-trigger the effect (with stale closure state)
  // and snap the selection to fallbackIso, which on "+1" is AG —
  // exactly the bug we are fixing. The ref records the last dial
  // we emitted so we can ignore the resulting prop echo.
  const dialFromIso = (iso: string) =>
    countries.find((c) => c.code === iso)?.dial || dialCode;
  const initialIso = useMemo(() => {
    const byDial = countries.find((c) => c.dial === dialCode);
    return byDial?.code || countries[0]?.code || "";
  }, [countries, dialCode]);
  const [selectedIso, setSelectedIso] = useState<string>(initialIso);
  const lastEmittedDialRef = useRef<string>(dialCode);
  useEffect(() => {
    // Skip the prop echo from our own emit.
    if (dialCode === lastEmittedDialRef.current) return;
    // External dial change (e.g. parent reset the phone number): if
    // it no longer matches the dial of our currently selected ISO,
    // pick the first ISO matching the new dial.
    const currentDial = dialFromIso(selectedIso);
    if (currentDial !== dialCode) {
      const next =
        countries.find((c) => c.dial === dialCode)?.code ||
        countries[0]?.code ||
        "";
      if (next) setSelectedIso(next);
      lastEmittedDialRef.current = dialCode;
    }
    // dialFromIso is a stable closure over `countries`; we list its
    // free vars in the deps so eslint stays happy.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dialCode, countries]);

  return (
    <div className={cn("flex min-w-0 flex-col gap-2 sm:flex-row", className)}>
      <div className="relative w-full shrink-0 sm:w-28">
      <span aria-hidden="true" className="pointer-events-none absolute inset-y-0 left-2 flex items-center gap-1.5 text-base md:text-sm">
        {flagEmoji(selectedIso)} {dialFromIso(selectedIso)}
      </span>
      <span aria-hidden="true" className="pointer-events-none absolute inset-y-0 right-2 flex items-center text-muted-foreground">⌄</span>
      <select
        value={selectedIso}
        onChange={(e) => {
          const iso = e.target.value;
          setSelectedIso(iso);
          const next = countries.find((c) => c.code === iso);
          if (next) {
            lastEmittedDialRef.current = next.dial;
            onDialCodeChange(next.dial);
          }
        }}
        className="w-full min-w-0 appearance-none rounded-md border border-border bg-transparent px-2 py-2 text-base text-transparent [&>option]:bg-background [&>option]:text-foreground md:text-sm"
        aria-label={t("verification.country_code")}
      >
        {countries.map((c) => (
          <option key={c.code} value={c.code}>
            {flagEmoji(c.code)} {c.name} ({c.dial})
          </option>
        ))}
      </select>
      </div>
      <div className="relative min-w-0 flex-1">
        <Phone className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <input
          id={inputId}
          type="tel"
          inputMode="tel"
          autoComplete="tel-national"
          autoFocus={autoFocus}
          required={required}
          value={localNumber}
          onChange={(e) => onLocalNumberChange(e.target.value.replace(/[^\d\s-]/g, ""))}
          placeholder={placeholder}
          className="w-full rounded-md border border-border bg-background py-2 pl-8 pr-3 text-base md:text-sm"
        />
      </div>
    </div>
  );
}
